import React, { MouseEvent, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { trackEvent } from '@xingternal/hops-tracking';
import { XingId } from '@xingternal/hub';
import { IconArrowRight, IconSync, IconTick } from '@xingternal/icons';
import { SkeletonBodyCopy } from '@xingternal/skeleton';
import { BodyCopy, Headline } from '@xingternal/typography';

import { useIsFormPageviewTracked } from '@/domain/job-application/contexts';
import { useProfileCompletionLazyQuery, useProfileCompletionQuery } from '@/graphql/generated';
import { useOnFocus } from '@/hooks';

import { getProfileUrlWithTracking } from '../helpers';
import {
  Status,
  getNormalizedToFiveMultipleScore,
  getProfileCompletionStatus,
  i18ns,
} from './profile-completion-helpers';

import * as S from './profile-completion.styles';

const ERROR_ICON_SIZE = 28;
const ICON_SIZE = 22;

type Props = {
  user?: XingId;
};

export const ProfileCompletion: React.FC<Props> = ({ user }) => {
  const { data, loading, error } = useProfileCompletionQuery({
    variables: { consumer: 'loggedin.web.jobs.job_application.center' },
  });

  const [getProfileCompletion, { loading: refetchLoading }] = useProfileCompletionLazyQuery({
    variables: { consumer: 'loggedin.web.jobs.job_application.center' },
    fetchPolicy: 'network-only',
  });
  const hasBeenFocused = useOnFocus();
  const isFormPageviewTracked = useIsFormPageviewTracked();

  const score = data?.viewer?.networkEfficiencyIndicator?.score;
  const normalizedScore = score ? Math.round(score * 100) : score === 0 ? 0 : undefined;
  const profileCompletionStatus: Status =
    loading || refetchLoading
      ? 'pending'
      : !normalizedScore || error
      ? 'failed'
      : getProfileCompletionStatus(normalizedScore);

  useEffect(() => {
    if (hasBeenFocused) {
      getProfileCompletion();
    }
  }, [getProfileCompletion, hasBeenFocused]);

  useEffect(() => {
    if (isFormPageviewTracked && profileCompletionStatus !== 'pending') {
      trackEvent('PropTrackAction', {
        PropTrackAction: 'jobs_neffi_result_available',
        PropContextDimension1: `jobs_neffi_displayed_${profileCompletionStatus}`,
        ...(normalizedScore !== undefined
          ? { PropContextDimension2: `jobs_neffi_score_${getNormalizedToFiveMultipleScore(normalizedScore)}` }
          : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormPageviewTracked, profileCompletionStatus]);

  const onRetryHandler = (e: MouseEvent): void => {
    e.preventDefault();
    trackEvent('PropTrackAction', { PropTrackAction: 'jobs_neffi_try_again' });
    getProfileCompletion();
  };

  const profileUrl =
    profileCompletionStatus !== 'completed' &&
    profileCompletionStatus !== 'pending' &&
    user &&
    getProfileUrlWithTracking({
      user,
      trackingParams: [
        'sc_o=jobs_neffi_result_accept',
        `sc_o_PropActionOrigin=jobs_neffi_accept_${profileCompletionStatus}`,
      ],
    });

  if (profileCompletionStatus === 'failed') {
    return (
      <S.ErrorContainer onClick={onRetryHandler}>
        <div>
          <BodyCopy size="small" noMargin lineHeight="dense">
            <FormattedMessage id="JOBS_POSTING_EASY_APPLY_FORM_PROFILE_COMPLETION_ERROR" />
          </BodyCopy>
          <BodyCopy size="small" noMargin lineHeight="dense" fontWeight="bold">
            <FormattedMessage id="JOBS_POSTING_EASY_APPLY_FORM_PROFILE_COMPLETION_RETRY" />
          </BodyCopy>
        </div>
        <IconSync width={ERROR_ICON_SIZE} height={ERROR_ICON_SIZE} />
      </S.ErrorContainer>
    );
  }

  if (profileCompletionStatus === 'pending') {
    return (
      <S.StyledCard>
        <SkeletonBodyCopy amountRows={2} data-testid="profile-completion-skeleton" />
      </S.StyledCard>
    );
  }

  return (
    <S.CardContainer
      href={profileUrl}
      linkProps={{ target: '_blank', 'data-cy': 'instant-apply-profile-preview-link' }}
      data-testid="profile-completion-status"
    >
      <div>
        <S.ScoreContainer>
          <Headline size="xxlarge" noMargin>
            {normalizedScore}
          </Headline>
          <S.Score size="small" noMargin={true}>
            %
          </S.Score>
        </S.ScoreContainer>
        <S.ScoreBar status={profileCompletionStatus} />
      </div>

      <S.TextContainer>
        <S.Title size="small" noMargin={true} l>
          <FormattedMessage id={i18ns[profileCompletionStatus].title} />
        </S.Title>
        <BodyCopy size="small" noMargin lineHeight="dense">
          <FormattedMessage id={i18ns[profileCompletionStatus].body} />
        </BodyCopy>
      </S.TextContainer>

      <div>
        {profileCompletionStatus === 'high' ? (
          <IconTick width={ICON_SIZE} height={ICON_SIZE} />
        ) : (
          <IconArrowRight width={ICON_SIZE} height={ICON_SIZE} />
        )}
      </div>
    </S.CardContainer>
  );
};
